import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import {
  meetingService,
  Meeting as ApiMeeting,
  MeetingApiParams,
  Pagination,
  MeetingsApiResponse,
} from '../../services/meetingService';
import { getUserFriendlyErrorMessage } from '../../utils/errorUtils';

const getCurrentUser = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedUser = window.localStorage.getItem('user');
    if (storedUser) {
      return JSON.parse(storedUser);
    }
  } catch (error) {
    console.error('Failed to parse user from localStorage', error);
  }

  return null;
};

const getSalesExecutiveIdParam = () => {
  const user = getCurrentUser();
  if (!user) {
    return undefined;
  }

  const roles: string[] = Array.isArray(user.roles) ? user.roles : [];
  const hasSalesRole = roles.includes('Sales');

  if (hasSalesRole && user.user_id) {
    return String(user.user_id);
  }

  return undefined;
};

// Helper function to get pagination from localStorage
const getStoredPagination = (): { page: number; limit: number } | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  try {
    const stored = localStorage.getItem('meetingPaginationState');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (
        typeof parsed.page === 'number' && 
        typeof parsed.limit === 'number' &&
        parsed.page >= 1 &&
        parsed.limit > 0
      ) {
        return { page: parsed.page, limit: parsed.limit };
      }
    }
  } catch (error) {
    console.error('Failed to parse pagination from localStorage', error);
  }
  
  return null;
};

type FilterType = 'recent' | 'client' | 'salesExecutive' | 'project' | null;
type GroupedMeetings = { [key: string]: ApiMeeting[] };

interface MeetingContextType {
  meetings: ApiMeeting[];
  allMeetingsFromApi: ApiMeeting[];
  isLoading: boolean;
  error: string | null;
  filteredMeetings: GroupedMeetings;
  activeFilter: FilterType;
  setActiveFilter: (filter: FilterType) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  currentMeeting: ApiMeeting | null;
  setCurrentMeeting: (meeting: ApiMeeting | null) => void;
  fetchMeetings: (params?: MeetingApiParams) => Promise<MeetingsApiResponse>;
  pagination: Pagination | null;
  isSearching: boolean;
  hasApiSearchResults: boolean;
  addMeetingToContext: (meeting: ApiMeeting) => void;
}

const MeetingContext = createContext<MeetingContextType | undefined>(undefined);

export const useMeetingContext = () => {
  const context = useContext(MeetingContext);
  if (!context) {
    throw new Error('useMeetingContext must be used within a MeetingProvider');
  }
  return context;
};

interface MeetingProviderProps {
  children: ReactNode;
}

export const MeetingProvider: React.FC<MeetingProviderProps> = ({ children }) => {
  const [allMeetingsFromApi, setAllMeetingsFromApi] = useState<ApiMeeting[]>([]);
  const [currentPageMeetings, setCurrentPageMeetings] = useState<ApiMeeting[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [fetchedPages, setFetchedPages] = useState<Set<number>>(new Set());

  const [activeFilter, setActiveFilter] = useState<FilterType>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMeeting, setCurrentMeeting] = useState<ApiMeeting | null>(null);

  // Ensures the initial page is fetched only once (guards against React
  // StrictMode's double effect invocation in development).
  const hasInitializedRef = useRef(false);
  // Tracks the previous search term so the "search cleared" branch only
  // re-fetches when an actual search is cleared — not on initial mount.
  const previousSearchRef = useRef('');

  const fetchMeetings = async (params?: MeetingApiParams): Promise<MeetingsApiResponse> => {
    
    setIsLoading(true);
    setError(null);
    try {
      // Get sales executive ID if user has Sales role
      const salesExecutiveId = getSalesExecutiveIdParam();
      
      const apiParams = {
        ...params,
        business_identifier: 'real_estate',
        ...(salesExecutiveId && { salesExecutiveId }),
      };
      
      const response = await meetingService.getMeetings(apiParams);
      
      // Since API doesn't return success field, check if we have data
      if (response.data && Array.isArray(response.data)) {
        setAllMeetingsFromApi(response.data);
        const currentPage = apiParams.page || 1;
        
        // Always set current page meetings (this is what gets displayed)
        setCurrentPageMeetings(response.data);
        
        // Accumulate all meetings for MeetingDetailsPage (so it can find meetings from any page)
        setAllMeetingsFromApi(prevMeetings => {       
          // Create a map of existing meetings by meeting_id to avoid duplicates
          const existingMeetingsMap = new Map(
            prevMeetings.map(meeting => [meeting.meeting_id, meeting])
          );
          
          // Add/update meetings from current response
          response.data.forEach(newMeeting => {
            existingMeetingsMap.set(newMeeting.meeting_id, newMeeting);
          });
          
          // Convert back to array and sort by date (newest first)
          const allMeetings = Array.from(existingMeetingsMap.values());
          const sortedMeetings = sortMeetingsByDate(allMeetings);
          
          return sortedMeetings;
        });
        
        // Mark this page as fetched
        if (!fetchedPages.has(currentPage)) {
          setFetchedPages(prev => new Set(Array.from(prev).concat(currentPage)));
        }
        
        setPagination(response.pagination);
        return response;
      } else {
        throw new Error('Invalid response format or no data received');
      }
    } catch (err: any) {
      console.error("Failed to fetch meetings:", err);
      const humanizedError = getUserFriendlyErrorMessage(err, 'Failed to fetch meetings');
      setError(humanizedError);
      
      // Don't clear existing meetings on error - keep accumulated data
      setPagination(null);
      
      // Return empty response structure on error
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          limit: 10
        }
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Separate function for API search that doesn't overwrite main data
  const searchMeetingsViaApi = async (searchTerm: string): Promise<ApiMeeting[]> => {
    try {
      // Clear pagination from localStorage when searching
      if (typeof window !== 'undefined') {
        localStorage.removeItem('meetingPaginationState');
      }
      
      // Get sales executive ID if user has Sales role
      const salesExecutiveId = getSalesExecutiveIdParam();
      
      const apiParams = {
        type: 'real_estate',
        page: 1,
        limit: 100,
        search: searchTerm.trim(),
        business_identifier: 'real_estate',
        ...(salesExecutiveId && { salesExecutiveId }),
      };
      
      const response = await meetingService.getMeetings(apiParams);
      
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }
      
      return [];
    } catch (error) {
      console.error('API search failed:', error);
      return [];
    }
  };

  // Reset accumulated meetings when filters change (to start fresh)
  const resetMeetings = () => {
    setAllMeetingsFromApi([]);
    setFetchedPages(new Set());
  };

  // Add a single meeting to the context (for fallback API)
  const addMeetingToContext = (meeting: ApiMeeting) => {
    setAllMeetingsFromApi(prevMeetings => {
      // Check if meeting already exists
      const existingMeetingsMap = new Map(
        prevMeetings.map(m => [m.meeting_id, m])
      );
      
      // Add or update the meeting
      existingMeetingsMap.set(meeting.meeting_id, meeting);
      
      const allMeetings = Array.from(existingMeetingsMap.values());
      const sortedMeetings = sortMeetingsByDate(allMeetings);
      
      return sortedMeetings;
    });
  };

  useEffect(() => {
    // Fetch the initial page only once for the provider's lifetime.
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    // Get sales executive ID if user has Sales role
    const salesExecutiveId = getSalesExecutiveIdParam();

    // Check localStorage for saved pagination state
    const storedPagination = getStoredPagination();
    const initialPage = storedPagination?.page || 1;
    const initialLimit = storedPagination?.limit || 10;

    fetchMeetings({
      type: 'real_estate',
      page: initialPage,
      limit: initialLimit,
      ...(salesExecutiveId && { salesExecutiveId }),
    });
  }, []);

  // State to track if we're searching via API
  const [isSearching, setIsSearching] = useState(false);
  const [apiSearchResults, setApiSearchResults] = useState<ApiMeeting[]>([]);
  const [hasApiSearchResults, setHasApiSearchResults] = useState(false);



  // Effect to set searching state immediately when user types
  useEffect(() => {
    if (searchTerm.trim()) {
      setIsSearching(true);
    }
  }, [searchTerm]);

  // Effect to handle search - search directly via API
  useEffect(() => {
    const previousSearch = previousSearchRef.current;
    previousSearchRef.current = searchTerm;

    const searchTimeout = setTimeout(async () => {
      if (searchTerm.trim()) {
        
        // Set searching state immediately when search starts
        setIsSearching(true);
        
        // Reset previous search states
        setHasApiSearchResults(false);
        setApiSearchResults([]);
        
        // Search directly via API
        try {
          const apiResults = await searchMeetingsViaApi(searchTerm);
          setApiSearchResults(apiResults);
          setHasApiSearchResults(apiResults.length > 0);
        } catch (error) {
          console.error('API search failed:', error);
          setApiSearchResults([]);
          setHasApiSearchResults(false);
        } finally {
          setIsSearching(false);
        }
      } else {
        // Search cleared, reset to normal state
        setApiSearchResults([]);
        setHasApiSearchResults(false);
        setIsSearching(false);

        // Only re-fetch the normal list when an actual search was cleared.
        // On initial mount (no previous search) the mount effect already
        // loaded the first page, so skip the duplicate request.
        if (!previousSearch.trim()) {
          return;
        }

        // Fetch normal paginated data - restore from localStorage if available
        const salesExecutiveId = getSalesExecutiveIdParam();
        const storedPagination = getStoredPagination();
        const page = storedPagination?.page || 1;
        const limit = storedPagination?.limit || 10;

        fetchMeetings({
          type: 'real_estate',
          page,
          limit,
          ...(salesExecutiveId && { salesExecutiveId }),
        });
      }
    }, 500); // Debounce search by 500ms

    return () => clearTimeout(searchTimeout);
  }, [searchTerm]);

  const meetings = allMeetingsFromApi;

  // Helper function to convert ISO date string to sortable format
  const getDateForSorting = (dateString: string) => {
    // Handle ISO date strings (e.g., "2025-05-31T11:24:08.873Z")
    if (dateString.includes('T') || dateString.includes('-')) {
      return new Date(dateString);
    }
    
    // Fallback for DD/MM/YYYY format (for backwards compatibility)
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in JavaScript Date
  };

  // Function to sort meetings by date (newest first)
  const sortMeetingsByDate = (meetings: ApiMeeting[]) => {
    return [...meetings].sort((a, b) => {
      const dateA = getDateForSorting(a.date);
      const dateB = getDateForSorting(b.date);
      return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
    });
  };

  const getFilteredAndGroupedMeetings = () => {
    let filtered: ApiMeeting[];

    // Determine which data to use based on search state
    if (searchTerm.trim()) {
      if (hasApiSearchResults) {
        // Use API search results
        filtered = [...apiSearchResults];
      } else {
        // No results found for search term
        filtered = [];
      }
    } else {
      // Use normal API data (not searching)
      filtered = [...allMeetingsFromApi];
    }
    
    if (!activeFilter || activeFilter === 'recent') {
      return { "Recent Meetings": filtered };
    }

    const grouped = filtered.reduce((acc: GroupedMeetings, meeting) => {
      let key = '';
      switch (activeFilter) {
        case 'client':
          key = meeting.client;
          break;
        case 'salesExecutive':
          key = meeting.salesExecutive;
          break;
        case 'project':
          key = meeting.project;
          break;
      }
      
      if (key) {
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(meeting);
      }
      return acc;
    }, {});

    // For each group, sort the meetings by date
    Object.keys(grouped).forEach(key => {
      grouped[key] = sortMeetingsByDate(grouped[key]);
    });

    return grouped;
  };

  const filteredMeetings = getFilteredAndGroupedMeetings();

  return (
    <MeetingContext.Provider
      value={{
        meetings,
        allMeetingsFromApi,
        isLoading,
        error,
        filteredMeetings,
        activeFilter,
        setActiveFilter,
        searchTerm,
        setSearchTerm,
        currentMeeting,
        setCurrentMeeting,
        fetchMeetings,
        pagination,
        isSearching,
        hasApiSearchResults,
        addMeetingToContext,
      }}
    >
      {children}
    </MeetingContext.Provider>
  );
};

export default MeetingContext; 